import { isEmpty } from 'lodash';

/**
 * Filter users based on selected disabilities
 * @param {Array} selectedDisabilities - Array of selected disability names
 * @returns {Function} A filter function that returns true for users with matching disabilities
 */
export const filterUserWithDisabilities = (selectedDisabilities) => {
  if (!selectedDisabilities || !Array.isArray(selectedDisabilities) || selectedDisabilities.length === 0) {
    return () => true;
  }

  return (user) => {
    // Check if user has the selectedDisabilities property
    if (!user || !user.selectedDisabilities || !Array.isArray(user.selectedDisabilities)) {
      return false;
    }

    // Return true if any of the user's disabilities match any of the selected disabilities
    return user.selectedDisabilities.some(disability => 
      selectedDisabilities.includes(disability)
    );
  };
};

/**
 * Filter users based on self identification tags
 * @param {Array} selfIdentificationTags - Array of selected self identification tag names
 * @returns {Function} A filter function that returns true for users with matching self identification tags
 */
export const filterUserWithSelfIdentification = (selfIdentificationTags) => {
  if (!selfIdentificationTags || !Array.isArray(selfIdentificationTags) || selfIdentificationTags.length === 0) {
    return () => true;
  }

  return (user) => {
    // Check if user has the selfIdentificationTags property
    if (!user || !user.selfIdentificationTags || !Array.isArray(user.selfIdentificationTags)) {
      return false;
    }

    // Return true if any of the user's self identification tags match any of the selected tags
    return user.selfIdentificationTags.some(tag => 
      selfIdentificationTags.includes(tag)
    );
  };
};

/**
 * Combined filter for both disabilities and self identification
 * @param {Object} demographicFilters - Object with selectedDisabilities and selfIdentificationTags arrays
 * @returns {Function} A filter function that returns true for users matching either filter
 */
export const filterUserWithDemographics = (demographicFilters) => {
  const { selectedDisabilities, selfIdentificationTags } = demographicFilters || {};
  
  const hasDisabilityFilters = selectedDisabilities && 
                               Array.isArray(selectedDisabilities) && 
                               selectedDisabilities.length > 0;
  
  const hasSelfIdFilters = selfIdentificationTags && 
                           Array.isArray(selfIdentificationTags) && 
                           selfIdentificationTags.length > 0;
  
  // If no filters are applied, return all users
  if (!hasDisabilityFilters && !hasSelfIdFilters) {
    return () => true;
  }
  
  return (user) => {
    // Check for disability matches if we have disability filters
    if (hasDisabilityFilters) {
      const disabilityFilter = filterUserWithDisabilities(selectedDisabilities);
      if (disabilityFilter(user)) {
        return true;
      }
    }
    
    // Check for self identification matches if we have self id filters
    if (hasSelfIdFilters) {
      const selfIdFilter = filterUserWithSelfIdentification(selfIdentificationTags);
      if (selfIdFilter(user)) {
        return true;
      }
    }
    
    // If we get here, the user didn't match any of our filters
    return !hasDisabilityFilters && !hasSelfIdFilters;
  };
};

export default filterUserWithDemographics;
